<template>
    <div>
        <div v-if="loading" class="text-center p-5"><b-spinner variant="info" /></div>
        <b-alert v-else-if="loadError" show variant="danger">
            {{ $t('label.orderLoadError') }}
            <b-button size="sm" variant="outline-danger" @click="loadOrder">{{ $t('label.retryPermissionDefinitions')
            }}</b-button>
        </b-alert>
        <form v-else @submit.prevent="submitForm">
            <b-alert v-if="orderUUID && !canEdit" show variant="info">{{ $t('label.orderReadOnly') }}</b-alert>
            <b-form-group :label="$t('label.orderBuyer')">
                <multiselect v-model="buyer" :options="buyerOptions" track-by="value" label="text" :allow-empty="false"
                    :disabled="!canEdit" :loading="buyerLoading" :internal-search="false"
                    @search-change="searchBuyers" />
            </b-form-group>
            <b-alert v-if="lookupError" show variant="warning">
                {{ $t('label.orderLookupError') }}
                <b-button type="button" size="sm" variant="outline-warning" @click="retryLookups">{{
                    $t('label.retryPermissionDefinitions') }}</b-button>
            </b-alert>
            <div class="d-flex justify-content-between align-items-center mb-2">
                <h6>{{ $t('label.orderItems') }}</h6>
                <b-button type="button" size="sm" variant="outline-info"
                    :disabled="!canEdit || items.length >= maxAllowedItems" @click="addItem">{{ $t('label.orderAddItem')
                    }}</b-button>
            </div>
            <div class="table-responsive">
                <table class="table table-bordered order-items">
                    <thead>
                        <tr>
                            <th>{{ $t('module.product') }}</th>
                            <th>{{ $t('label.productVariations') }}</th>
                            <th>{{ $t('label.quantity') }}</th>
                            <th>{{ $t('label.price') }}</th>
                            <th>{{ $t('label.amount') }}</th>
                            <th>{{ $t('label.action') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in items" :key="item.key">
                            <td>
                                <multiselect v-floating-select v-model="item.product" :options="productOptions"
                                    track-by="value" label="text" :allow-empty="false" :internal-search="false"
                                    :disabled="!canEdit" :loading="productLoading" open-direction="below"
                                    @search-change="searchProducts" @input="productChanged(item, $event)" />
                            </td>
                            <td>
                                <multiselect v-floating-select v-model="item.variation" :options="item.variationOptions"
                                    track-by="value" label="text" :allow-empty="false" :loading="item.loading"
                                    open-direction="below" :disabled="!canEdit || !item.product || item.loading"
                                    @input="variationChanged(item, $event)" />
                            </td>
                            <td><b-form-input v-model.number="item.quantity" type="number" min="0" step="any" required
                                    :disabled="!canEdit" /></td>
                            <td><b-form-input v-model="item.price" type="number" min="0" step="0.01" required
                                    disabled /></td>
                            <td>{{ lineAmount(item) }}</td>
                            <td><b-button type="button" size="sm" variant="outline-danger"
                                    :aria-label="$t('label.delete')" :disabled="!canEdit"
                                    @click="removeItem(item.key)"><i class="mdi mdi-delete-outline"></i></b-button></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="text-right font-weight-bold">{{ $t('label.totalAmount') }}: {{ totalAmount }}</div>
            <b-alert v-if="validationError" show variant="danger" class="mt-3">{{ validationError }}</b-alert>
            <hr>
            <div class="d-flex justify-content-end">
                <b-button type="button" variant="danger" class="mr-2" @click="$emit('done')">{{ $t('label.cancel')
                }}</b-button>
                <b-button v-if="canEdit" type="submit" variant="info"
                    :disabled="!status.save || submitting || items.some(item => item.loading)">
                    <span v-if="status.save">{{ $t(orderUUID ? 'label.updateX' : 'label.createX', [$t('module.order')])
                    }}</span><b-spinner v-else small />
                </b-button>
            </div>
        </form>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import Multiselect from 'vue-multiselect';
import floatingSelect from '@/directives/floating-select';
import { orderMethods } from '@/state/api';
import { lookupService } from '@/services/api/lookup.service';
import { productService } from '@/services/api/product.service';
import { orderTotals } from '@/utils/order.helper';
import { MAX_ORDER_ITEMS } from '@/constants/order';

let itemKey = 0;
function newItem() { return { key: ++itemKey, product: null, variation: null, variationOptions: [], quantity: 1, price: '', loading: false, request: 0 }; }
function lookupItems(result) {
    if (result?.success === false) throw new Error('Lookup failed.');
    const data = result?.data?.data || result?.data || result;
    if (!Array.isArray(data)) throw new Error('Invalid lookup response.');
    return data;
}
export default {
    name: 'OrderForm', components: { Multiselect },
    directives: { floatingSelect },
    props: { orderUUID: { type: String, default: '' }, searchCriteria: { type: Object, required: true } },
    data() {
        return {
            itemLimit: MAX_ORDER_ITEMS, originalItemCount: 0,
            loadedStatus: null, submitting: false,
            loading: Boolean(this.orderUUID), loadError: false, lookupError: false, validationError: '',
            buyer: null, buyerOptions: [], buyerLoading: false, buyerRequest: 0, buyerTimer: null,
            items: [newItem()], productOptions: [], productLoading: false, productRequest: 0, productTimer: null,
        };
    },
    computed: {
        maxAllowedItems() { return Math.max(this.itemLimit, this.orderUUID ? this.originalItemCount : 0); },
        canEdit() { return !this.orderUUID || this.loadedStatus === 'New'; },
        ...mapState('api/order', ['status', 'details']),
        totalAmount() {
            try { return orderTotals(this.items.map(item => ({ uuid: item.variation?.value, quantity: item.quantity, price: item.price }))).total_amount.toFixed(2); }
            catch (error) { return '0.00'; }
        },
    },
    created() { this.retryLookups(); if (this.orderUUID) this.loadOrder(); },
    beforeDestroy() { clearTimeout(this.buyerTimer); clearTimeout(this.productTimer); this.buyerRequest++; this.productRequest++; },
    methods: {
        ...orderMethods,
        addItem() {
            if (this.canEdit && this.items.length < this.maxAllowedItems) {
                this.items.push(newItem())
            }
        },
        removeItem(key) {
            if (this.canEdit) {
                this.items = this.items.filter(item => item.key !== key)
            }
        },
        lineAmount(item) {
            try {
                return orderTotals([{ uuid: item.variation?.value, quantity: item.quantity, price: item.price }]).total_amount.toFixed(2);
            }
            catch (error) {
                return '0.00';
            }
        },
        async retryLookups() { this.lookupError = false; await Promise.all([this.loadBuyers(), this.loadProducts()]); },
        async loadBuyers(term = '') {
            const request = ++this.buyerRequest; this.buyerLoading = true;
            try {
                const result = await lookupService.getUsers({ searchWords: term ? [term] : [] });
                if (request !== this.buyerRequest) return;

                this.buyerOptions = lookupItems(result).map(user => ({
                    value: user.uuid,
                    text: user.name || user.full_name || [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || user.uuid
                }));
            } catch (error) {
                if (request === this.buyerRequest) {
                    this.lookupError = true
                }
            }
            finally {
                if (request === this.buyerRequest) {
                    this.buyerLoading = false
                }
            }
        },
        async loadProducts(term = '') {
            const request = ++this.productRequest;
            this.productLoading = true;
            try {
                const result = await productService.getAll({ pageNumber: 1, pageSize: 15, searchArray: term ? [term] : [] });
                if (request !== this.productRequest) return;

                this.productOptions = lookupItems(result).map(product => ({ value: product.uuid, text: product.name }));
            } catch (error) {
                if (request === this.productRequest) {
                    this.lookupError = true;
                }
            }
            finally {
                if (request === this.productRequest) {
                    this.productLoading = false;
                }
            }
        },
        searchBuyers(term) {
            clearTimeout(this.buyerTimer);
            this.buyerRequest++;
            this.buyerTimer = setTimeout(() => this.loadBuyers(term), 350);
        },
        searchProducts(term) {
            clearTimeout(this.productTimer); this.productRequest++; this.productTimer = setTimeout(() => this.loadProducts(term), 350);
        },
        variationChanged(item, variation) {
            if (!this.canEdit) return;
            item.price = variation?.price ?? '';
        },
        async productChanged(item, product) {
            if (!this.canEdit) return;
            item.variation = null; item.variationOptions = []; item.price = '';
            const request = ++item.request;
            if (!product) return;
            item.loading = true;
            try {
                const result = await productService.show(product.value);

                if (request !== item.request) return;

                if (!result?.success) throw new Error('Product load failed.');

                const data = result.data?.product || result.data;

                item.variationOptions = (data.product_variations || []).filter(row => [true, 1, '1'].includes(row.is_active))
                    .map(row => ({ value: row.uuid, text: row.name, price: row.selling_price }));
                if (item.variationOptions.length === 1) {
                    item.variation = item.variationOptions[0]; this.variationChanged(item, item.variation);
                }
            } catch (error) {
                if (request === item.request) {
                    this.validationError = this.$t('label.orderVariationLoadError');
                }
            }
            finally {
                if (request === item.request) {
                    item.loading = false;
                }
            }
        },
        async loadExistingVariations(item) {
            if (!item.product) return;
            const request = ++item.request;
            item.loading = true;
            try {
                const result = await productService.show(item.product.value);
                if (request !== item.request) return;

                if (!result?.success) {
                    throw new Error('Product load failed.');
                }

                const data = result.data?.product || result.data;
                const options = (data.product_variations || []).filter(row => [true, 1, '1'].includes(row.is_active))
                    .map(row => ({ value: row.uuid, text: row.name, price: row.selling_price }));

                if (item.variation && !options.some(option => option.value === item.variation.value)) {
                    options.push(item.variation);
                }
                item.variationOptions = options;
                // Retain the order's historical price until the user changes the variation.
            } catch (error) {
                if (request === item.request) {
                    this.validationError = this.$t('label.orderVariationLoadError');
                }
            }
            finally {
                if (request === item.request) {
                    item.loading = false;
                }
            }
        },
        async loadOrder() {
            this.loading = true; this.loadError = false;
            try {
                const result = await this.getOrder({ uuid: this.orderUUID });
                if (!result?.success) {
                    this.loadError = true;
                    return;
                }
                this.loadedStatus = this.details.statusName;
                this.originalItemCount = this.details.items.length;
                this.buyer = this.details.buyerUUID ? { value: this.details.buyerUUID, text: this.details.buyerName || this.details.buyerEmail } : null;
                this.items = this.details.items.map(detail => {
                    const variation = detail.uuid ? { value: detail.uuid, text: detail.variation?.name || detail.uuid, price: detail.price } : null;
                    return {
                        ...newItem(),
                        product: detail.product ? { value: detail.product.uuid, text: detail.product.name } : null,
                        variation,
                        variationOptions: variation ? [variation] : [],
                        quantity: detail.quantity,
                        price: detail.price
                    };
                });
                await Promise.all(this.items.map(item => this.loadExistingVariations(item)));
            } catch (error) {
                this.loadError = true;
            }
            finally {
                this.loading = false;
            }
        },
        async submitForm() {
            if (!this.canEdit || this.submitting || !this.status.save || this.items.some(item => item.loading)) return;
            this.validationError = '';
            if (this.items.length > this.maxAllowedItems) {
                this.validationError = this.$t('label.orderItemLimit', [this.maxAllowedItems]);
                return;
            }
            if (!this.buyer?.value) {
                this.validationError = this.$t('label.orderBuyerRequired');
                return;
            }
            const items = this.items.map(item => ({ uuid: item.variation?.value, quantity: item.quantity, price: item.price }));
            try {
                orderTotals(items);
            }
            catch (error) {
                this.validationError = this.$t('label.orderItemsInvalid');
                return;
            }
            this.submitting = true;
            try {
                if (this.orderUUID) {
                    const latest = await this.getOrder({ uuid: this.orderUUID });
                    if (!latest?.success) {
                        this.validationError = this.$t('label.orderLoadError');
                        return;
                    }
                    this.loadedStatus = this.details.statusName;
                    if (this.loadedStatus !== 'New') {
                        this.validationError = this.$t('label.orderReadOnly');
                        return;
                    }
                }
                const action = this.orderUUID ? this.updateOrder : this.createOrder;
                const result = await action({ data: { uuid: this.orderUUID, buyerUUID: this.buyer.value, items }, filter: this.searchCriteria });
                if (result?.success) {
                    this.$emit('done');
                }
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>

<style scoped>
.order-items {
    min-width: 940px;
}

.order-items th,
.order-items td {
    min-width: 110px;
    vertical-align: middle;
}

.order-items th:nth-child(1),
.order-items td:nth-child(1) {
    min-width: 230px;
}

.order-items th:nth-child(2),
.order-items td:nth-child(2) {
    min-width: 180px;
}
</style>
